/**
 * Alert routing, editable.
 *
 * Routing has always been data. What it has never had is a surface, so changing
 * how a tenant is notified meant a migration or a psql session — and the one
 * subsystem whose whole job is "does anybody hear about this" was the one
 * nobody could adjust.
 *
 * ── The chain is shown, not just the rows ────────────────────────────────
 *
 * A rule only means something in relation to the ones around it: the resolver
 * takes the FIRST key that has a row, walking `cause:CLASS/cause` →
 * `class:CLASS` → `*`. A flat list of rules invites somebody to add
 * `cause:X/y` expecting it to add to `class:X` rather than replace it. So the
 * default is drawn as the floor everything falls through to, and each rule says
 * what it overrides.
 *
 * ── Keys come from a list, never from typing ─────────────────────────────
 *
 * 🔴 The failure this subsystem is prone to is a key that matches no chain the
 * resolver builds. Such a row is completely inert and looks exactly like
 * configuration. It has happened once already, in the seed. The server
 * validates, and this screen never offers a free-text key — the classes and
 * causes come from the same response as the rules.
 *
 * ── Two things are refused rather than hidden ────────────────────────────
 *
 * The default rule cannot be deleted, and cannot be set to inherit its targets.
 * Both are drawn as disabled controls with the reason attached rather than
 * simply absent, because "I cannot find the delete button" and "this must not
 * be deleted" are different things to learn.
 *
 * ── Shape ────────────────────────────────────────────────────────────────
 *
 * A read-only table, one row per rule, the default last; the row opens a
 * drawer holding the edit form, and Save sends the rule and then its targets
 * in that order — the same order the per-cell controls used to send them in.
 * New rule is the page's single primary action and also opens a drawer.
 */

import { Plus } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { ApiError, api } from './api.ts';
import {
  Banner,
  Button,
  DataTable,
  Drawer,
  Field,
  Loading,
  Pill,
  Section,
  Toolbar,
  type ColumnDef,
} from './components/index.ts';

type Delivery = 'instant' | 'digest' | 'off';
type Severity = 'info' | 'warning' | 'critical';
type TargetsMode = 'inherit' | 'explicit';

interface Channel {
  id: string;
  label: string;
  kind: string;
  enabled: boolean;
  verifiedAt: string | null;
}

interface Rule {
  key: string;
  label: string | null;
  delivery: Delivery;
  minSeverity: Severity;
  targetsMode: TargetsMode;
  updatedAt: string;
  targets: Channel[];
}

interface RoutingResponse {
  rules: Rule[];
  classes: Array<{ key: string; label: string }>;
  causes: Array<{ class_key: string; key: string; label: string }>;
  channels: Channel[];
}

const DELIVERIES: Delivery[] = ['instant', 'digest', 'off'];
const SEVERITIES: Severity[] = ['info', 'warning', 'critical'];

/** Sorted so the default sits last — it is the floor, not the first rule. */
function ordered(rules: readonly Rule[]): Rule[] {
  return [...rules].sort((a, b) => {
    if (a.key === '*') return 1;
    if (b.key === '*') return -1;
    return a.key.localeCompare(b.key);
  });
}

function describeKey(key: string): string {
  if (key === '*') return 'Everything else';
  const cause = /^cause:([^/]+)\/(.+)$/.exec(key);
  if (cause !== null) return `${cause[1]} · ${cause[2]}`;
  const cls = /^class:(.+)$/.exec(key);
  if (cls !== null) return cls[1] ?? key;
  return key;
}

/**
 * The trap this subsystem sets: a rule whose floor is above the severity of
 * the alerts it covers reaches nobody at all, and looks configured. Said on
 * the row and in the drawer rather than discovered.
 */
function dropsWarnings(delivery: Delivery, minSeverity: Severity): boolean {
  return minSeverity === 'critical' && delivery !== 'off';
}

const DROPS_WARNINGS =
  'Warnings under this rule reach nobody: the floor is critical, so only critical alerts are sent.';

const DEFAULT_MUST_NAME =
  'The default rule must name its recipients — there is nothing further out for it to inherit from.';

const DEFAULT_CANNOT_GO =
  'The default cannot be removed: without it every alert for this tenant reaches nobody.';

function messageOf(err: unknown): string {
  return err instanceof ApiError ? err.message : String(err);
}

/** `off` is a decision, not an absence, so it is the one delivery drawn loudly. */
function DeliveryPill({ delivery }: { delivery: Delivery }): React.JSX.Element {
  if (delivery === 'off') {
    return (
      <Pill state="warning" reason="Alerts under this rule are deliberately not sent to anybody.">
        off
      </Pill>
    );
  }
  return <Pill state="neutral">{delivery}</Pill>;
}

export function RoutingView(): React.JSX.Element {
  const [data, setData] = useState<RoutingResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await api.get<RoutingResponse>('/api/routing'));
      setError(null);
    } catch (err) {
      setError(messageOf(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const rules = useMemo(() => ordered(data?.rules ?? []), [data]);

  const columns = useMemo<ColumnDef<Rule, unknown>[]>(
    () => [
      {
        id: 'key',
        header: 'Applies to',
        accessorKey: 'key',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="stack-tight">
            <span className="row">
              <strong>{describeKey(row.original.key)}</strong>
              {row.original.key === '*' && (
                <Pill state="neutral" reason="The floor: every alert no other rule claims falls through to this one.">
                  default
                </Pill>
              )}
            </span>
            <code className="small muted">{row.original.key}</code>
          </div>
        ),
      },
      {
        id: 'delivery',
        header: 'Delivery',
        accessorKey: 'delivery',
        cell: ({ row }) => <DeliveryPill delivery={row.original.delivery} />,
        size: 110,
      },
      {
        id: 'minSeverity',
        header: 'At least',
        accessorFn: (r) => SEVERITIES.indexOf(r.minSeverity),
        cell: ({ row }) => (
          <span className="row">
            {row.original.minSeverity}
            {dropsWarnings(row.original.delivery, row.original.minSeverity) && (
              <Pill state="warning" reason={DROPS_WARNINGS}>
                warnings dropped
              </Pill>
            )}
          </span>
        ),
        size: 180,
      },
      {
        id: 'targets',
        header: 'Recipients',
        accessorFn: (r) => (r.targetsMode === 'inherit' ? -1 : r.targets.length),
        cell: ({ row }) => <TargetsCell rule={row.original} />,
      },
      {
        id: 'updated',
        header: 'Updated',
        accessorKey: 'updatedAt',
        cell: ({ getValue }) => (
          <span className="muted">{new Date(getValue() as string).toLocaleString()}</span>
        ),
        size: 170,
      },
    ],
    [],
  );

  if (error !== null && data === null) return <Banner tone="fault">{error}</Banner>;
  if (data === null) return <Loading rows={4} cols={4} label="Loading routing" />;

  const existing = new Set(data.rules.map((r) => r.key));
  const candidates = [
    ...data.classes.map((c) => ({ key: `class:${c.key}`, label: `${c.label} (whole class)` })),
    ...data.causes.map((c) => ({
      key: `cause:${c.class_key}/${c.key}`,
      label: `${c.class_key} · ${c.label}`,
    })),
  ].filter((c) => !existing.has(c.key));

  const current = selected === null ? undefined : data.rules.find((r) => r.key === selected);

  return (
    <>
      <Toolbar
        end={
          <Button variant="primary" icon={<Plus size={16} />} onClick={() => setCreating(true)}>
            New rule
          </Button>
        }
      />

      {error !== null && <Banner tone="fault">{error}</Banner>}

      <Section
        flush
        title="Alert routing"
        summary="The most specific rule that exists wins: a cause overrides its class, and a class overrides the default."
      >
        <DataTable
          data={rules}
          columns={columns}
          getRowId={(r) => r.key}
          onRowClick={(r) => setSelected(r.key)}
          rowClass={(r) => (r.key === '*' ? 'row-muted' : undefined)}
          caption="Alert routing rules"
        />
      </Section>

      {current !== undefined && (
        <RuleDrawer
          key={current.key}
          rule={current}
          channels={data.channels}
          onClose={() => setSelected(null)}
          onChanged={load}
        />
      )}

      <NewRuleDrawer
        open={creating}
        candidates={candidates}
        onClose={() => setCreating(false)}
        onCreated={load}
      />
    </>
  );
}

/** Who a rule reaches, as the row shows it. */
function TargetsCell({ rule }: { rule: Rule }): React.JSX.Element {
  if (rule.targetsMode === 'inherit') {
    return (
      <span className="row">
        <Pill state="neutral" reason="Recipients come from the rule outside this one.">
          inherits
        </Pill>
      </span>
    );
  }
  if (rule.targets.length === 0) {
    return (
      <Pill state="fault" reason="No recipients are named, so this rule reaches nobody.">
        nobody
      </Pill>
    );
  }
  return (
    <span>
      {rule.targets.map((c, i) => (
        <span key={c.id}>
          {i > 0 ? ', ' : ''}
          {c.label}
          {/* A channel nobody has verified has never been proved to reach
              anywhere. A dead-man's switch once wrote CRITICAL to a host with
              no mail server for 59 consecutive nights and nothing could tell. */}
          {c.verifiedAt === null && <span className="muted"> · unverified</span>}
          {c.enabled ? null : <span className="muted"> · disabled</span>}
        </span>
      ))}
    </span>
  );
}

/** What a rule is being edited into, before it is saved. */
interface Draft {
  delivery: Delivery;
  minSeverity: Severity;
  targetsMode: TargetsMode;
  targetIds: ReadonlySet<string>;
}

function draftOf(rule: Rule): Draft {
  return {
    delivery: rule.delivery,
    minSeverity: rule.minSeverity,
    targetsMode: rule.targetsMode,
    targetIds: new Set(rule.targets.map((t) => t.id)),
  };
}

function sameIds(a: ReadonlySet<string>, b: ReadonlySet<string>): boolean {
  if (a.size !== b.size) return false;
  for (const id of a) if (!b.has(id)) return false;
  return true;
}

function RuleDrawer({
  rule,
  channels,
  onClose,
  onChanged,
}: {
  rule: Rule;
  channels: Channel[];
  onClose: () => void;
  onChanged: () => Promise<void>;
}): React.JSX.Element {
  const [draft, setDraft] = useState<Draft>(() => draftOf(rule));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isDefault = rule.key === '*';
  const edit = (patch: Partial<Draft>): void => setDraft((d) => ({ ...d, ...patch }));

  const toggleTarget = (id: string): void => {
    const next = new Set(draft.targetIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    edit({ targetIds: next });
  };

  const save = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await api.put(`/api/routing/${encodeURIComponent(rule.key)}`, {
        label: rule.label,
        delivery: draft.delivery,
        minSeverity: draft.minSeverity,
        targetsMode: draft.targetsMode,
      });
      // Blank recipients means inherit: an explicit list is only sent when the
      // rule names its own, and only when the list actually changed.
      if (draft.targetsMode === 'explicit' && !sameIds(draft.targetIds, draftOf(rule).targetIds)) {
        await api.put(`/api/routing/${encodeURIComponent(rule.key)}/targets`, {
          channelIds: channels.filter((c) => draft.targetIds.has(c.id)).map((c) => c.id),
        });
      }
      await onChanged();
      onClose();
    } catch (err) {
      // Surfaced, not swallowed: the server refuses keys that would be inert,
      // and an empty explicit list on a rule that is not off, and says why.
      // That reason is the useful part.
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await api.del(`/api/routing/${encodeURIComponent(rule.key)}`);
      await onChanged();
      onClose();
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  };

  const explicit = draft.targetsMode === 'explicit';
  const reachesNobody = explicit && draft.targetIds.size === 0 && draft.delivery !== 'off';

  return (
    <Drawer open onClose={onClose} title={describeKey(rule.key)} subtitle={<code>{rule.key}</code>}>
      <div className="stack form-narrow">
        {error !== null && <Banner tone="fault">{error}</Banner>}
        {dropsWarnings(draft.delivery, draft.minSeverity) && <Banner tone="warn">{DROPS_WARNINGS}</Banner>}
        {reachesNobody && (
          <Banner tone="warn">No recipients are named, so this rule reaches nobody.</Banner>
        )}

        <Field id="rule-delivery" label="Delivery">
          <select
            id="rule-delivery"
            value={draft.delivery}
            disabled={busy}
            onChange={(e) => edit({ delivery: e.target.value as Delivery })}
          >
            {DELIVERIES.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </Field>

        <Field id="rule-min-severity" label="At least">
          <select
            id="rule-min-severity"
            value={draft.minSeverity}
            disabled={busy}
            onChange={(e) => edit({ minSeverity: e.target.value as Severity })}
          >
            {SEVERITIES.map((sev) => (
              <option key={sev} value={sev}>
                {sev}
              </option>
            ))}
          </select>
        </Field>

        <Field
          id="rule-targets-mode"
          label="Recipients"
          hint={isDefault ? DEFAULT_MUST_NAME : 'Inherit takes them from the rule outside this one.'}
        >
          <select
            id="rule-targets-mode"
            value={draft.targetsMode}
            disabled={busy || isDefault}
            {...(isDefault ? { title: DEFAULT_MUST_NAME } : {})}
            onChange={(e) => edit({ targetsMode: e.target.value as TargetsMode })}
          >
            <option value="inherit">inherit</option>
            <option value="explicit">explicit</option>
          </select>
        </Field>

        {explicit && (
          <ul className="check-list" aria-label="Recipient channels">
            {channels.map((c) => {
              const id = `rule-target-${c.id}`;
              return (
                <li key={c.id}>
                  <input
                    id={id}
                    type="checkbox"
                    checked={draft.targetIds.has(c.id)}
                    disabled={busy}
                    onChange={() => toggleTarget(c.id)}
                  />
                  {/* htmlFor rather than wrapping — see Field's docstring on
                      why a nested control in a label misbehaves here. */}
                  <label htmlFor={id}>
                    {c.label}
                    {c.verifiedAt === null && <span className="muted small"> · unverified</span>}
                    {c.enabled ? null : <span className="muted small"> · disabled</span>}
                  </label>
                </li>
              );
            })}
          </ul>
        )}

        <div className="row">
          <Button variant="primary" busy={busy} onClick={() => void save()}>
            Save
          </Button>
          <Button variant="ghost" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            busy={busy}
            disabled={isDefault}
            title={isDefault ? DEFAULT_CANNOT_GO : 'Remove this rule, so it falls through to the one outside it.'}
            onClick={() => void remove()}
          >
            Remove
          </Button>
        </div>
      </div>
    </Drawer>
  );
}

function NewRuleDrawer({
  open,
  candidates,
  onClose,
  onCreated,
}: {
  open: boolean;
  candidates: Array<{ key: string; label: string }>;
  onClose: () => void;
  onCreated: () => Promise<void>;
}): React.JSX.Element {
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = (): void => {
    setKey('');
    setError(null);
    onClose();
  };

  const create = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await api.put(`/api/routing/${encodeURIComponent(key)}`, {
        // Inherits recipients and matches the seeded default's floor:
        // a new rule should change nothing until somebody changes it.
        label: null,
        delivery: 'digest',
        minSeverity: 'critical',
        targetsMode: 'inherit',
      });
      await onCreated();
      close();
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={close}
      title="New rule"
      subtitle="Starts by inheriting its recipients, so it changes nothing until you change it."
    >
      <div className="stack form-narrow">
        {error !== null && <Banner tone="fault">{error}</Banner>}

        {/*
          Chosen from the classes and causes that exist. A key that matches
          nothing would be accepted by the table and ignored by the resolver,
          so it is not offered.
        */}
        <Field
          id="new-rule-key"
          label="Applies to"
          hint="Only classes and causes that exist are offered; a key that matched nothing would be silently inert."
        >
          <select id="new-rule-key" value={key} disabled={busy} onChange={(e) => setKey(e.target.value)}>
            <option value="">Choose…</option>
            {candidates.map((c) => (
              <option key={c.key} value={c.key}>
                {c.label}
              </option>
            ))}
          </select>
        </Field>

        <div className="row">
          <Button variant="primary" busy={busy} disabled={key === ''} onClick={() => void create()}>
            Create
          </Button>
          <Button variant="ghost" disabled={busy} onClick={close}>
            Cancel
          </Button>
        </div>
      </div>
    </Drawer>
  );
}
